import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Typography, Box, LinearProgress } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { CenterDialog } from '../../dummies/CenterDialog/CenterDialog';
import { handleInactivityLogout } from './actions/inactivityActions';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 דקות
const WARNING_DURATION = 30 * 1000; // 30 שניות

/**
 * InactivityMonitor - מנטר חוסר פעילות
 * מזהה חוסר פעילות של 5 דקות ומציג אזהרה למשך 30 שניות
 * 
 * @param {function} dispatch - Redux dispatch function
 */
function InactivityMonitor({ dispatch }) {
    const [showWarning, setShowWarning] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(30);

    const inactivityTimerRef = useRef(null);
    const warningTimerRef = useRef(null);
    const lastActivityTimeRef = useRef(Date.now());
    const warningStartTimeRef = useRef(null);

    // בדיקה מתמדת של הזמן - גם כשהטאב לא פעיל
    useEffect(() => {
        const checkInactivity = () => {
            const now = Date.now();
            const timeSinceLastActivity = now - lastActivityTimeRef.current;

            // אם עברו יותר מ-5 דקות + 30 שניות - logout מיידי
            if (timeSinceLastActivity >= INACTIVITY_TIMEOUT + WARNING_DURATION) {
                handleInactivityLogout(dispatch);
                return;
            }

            // אם עברו יותר מ-5 דקות - הצג אזהרה
            if (timeSinceLastActivity >= INACTIVITY_TIMEOUT && !showWarning) {
                setShowWarning(true);
                warningStartTimeRef.current = now;

                const remaining = Math.max(0, Math.floor((WARNING_DURATION - (now - warningStartTimeRef.current)) / 1000));
                setRemainingSeconds(remaining);
            }

            // אם האזהרה פתוחה - עדכן את הספירה לאחור
            if (showWarning && warningStartTimeRef.current) {
                const timeInWarning = now - warningStartTimeRef.current;
                const remaining = Math.max(0, Math.floor((WARNING_DURATION - timeInWarning) / 1000));
                setRemainingSeconds(remaining);

                if (remaining <= 0) {
                    handleInactivityLogout(dispatch);
                }
            }
        };

        // בדוק כל שנייה
        const interval = setInterval(checkInactivity, 1000);

        return () => clearInterval(interval);
    }, [dispatch, showWarning]);

    // ספירה לאחור - מוסר, כי עכשיו checkInactivity מטפל בזה
    // useEffect שמאזין לפעילות משתמש

    // איפוס טיימרים
    const resetTimers = useCallback(() => {
        // אם הפופ-אפ כבר פתוח, אל תאפס - תן למשתמש ללחוץ על הכפתור
        if (showWarning) {
            return;
        }

        // עדכן את זמן הפעילות האחרון
        lastActivityTimeRef.current = Date.now();

        // נקה טיימרים קיימים
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
        }

        // סגור אזהרה אם פתוחה
        setShowWarning(false);
        setRemainingSeconds(30);
        warningStartTimeRef.current = null;
    }, [showWarning]);

    // מאזינים לפעילות משתמש
    useEffect(() => {
        const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];

        const handleActivity = () => {
            resetTimers();
        };

        // הוסף מאזינים
        events.forEach(event => {
            document.addEventListener(event, handleActivity);
        });

        // התחל טיימר ראשוני
        resetTimers();

        // ניקוי
        const inactivityTimer = inactivityTimerRef.current;
        const warningTimer = warningTimerRef.current;
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });

            if (inactivityTimer) clearTimeout(inactivityTimer);
            if (warningTimer) clearTimeout(warningTimer);
        };
    }, [resetTimers]);

    // טיפול בלחיצה על "המשך עבודה"
    const handleContinue = () => {
        // עדכן את זמן הפעילות האחרון
        lastActivityTimeRef.current = Date.now();

        // נקה טיימרים
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
        }

        // סגור את הפופ-אפ
        setShowWarning(false);
        setRemainingSeconds(30);
        warningStartTimeRef.current = null;
    };

    const progress = (remainingSeconds / 30) * 100;

    return (
        <CenterDialog
            open={showWarning}
            onClose={() => { }} // חסום סגירה - רק דרך הכפתור
            onConfirm={handleContinue}
            title={
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <WarningAmberIcon sx={{ fontSize: 32, color: '#f59e0b' }} />
                    <span>אזהרה - חוסר פעילות</span>
                </Box>
            }
            confirmText="המשך עבודה"
            cancelText="" // ביטול כפתור הביטול
            confirmButtonColor="#1e3a5f"
            hideActions={false}
            minWidth="400px"
            maxWidth="500px"
        >
            <Box sx={{ py: 2 }}>
                <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                    לא זוהתה פעילות במערכת במשך 5 דקות
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    המערכת תנתק אותך אוטומטית בעוד:
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            color: remainingSeconds > 10 ? '#f59e0b' : '#dc2626',
                            fontWeight: 'bold',
                            fontSize: '3rem'
                        }}
                    >
                        {remainingSeconds}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        שניות
                    </Typography>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#e5e7eb',
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: remainingSeconds > 10 ? '#f59e0b' : '#dc2626',
                            transition: 'background-color 0.3s ease'
                        }
                    }}
                />
            </Box>
        </CenterDialog>
    );
}

export default InactivityMonitor;
